import { ErrorMessage } from "@/components/Message";
import { useEnvironmentLookupResult } from "@/utils/hooks/use-environment-lookup-result";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { Routes } from "@/utils/routes";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import React from "react";

export interface EnvironmentNotFoundErrorProps {}

/**
 * Global banner for an environment id in the url that the loaded team cannot resolve
 * under the url's project. Renders in the same layout slot as
 * `SuspendedEnvironmentWarning`.
 */
export const EnvironmentNotFoundError: React.FC<EnvironmentNotFoundErrorProps> = ({}) => {
  const { environmentId } = useRouteInfo();
  const lookup = useEnvironmentLookupResult();

  if (environmentId == null) {
    return null;
  }

  if (lookup.status === "project-mismatch") {
    return (
      <ErrorMessage title={"Environment/Project mismatch detected"} compact>
        This environment belongs to a separate project.{" "}
        {/* Inherit the banner's error color rather than using `.link`: bright green reads as
            a success accent against the red surface, and is not contrast-checked on it. */}
        <ChalkLink
          className={"font-medium underline underline-offset-2 hover:no-underline"}
          href={Routes.project.environment.root(lookup.correctProjectId, environmentId)}
        >
          Click here
        </ChalkLink>{" "}
        to go to the correct page.
      </ErrorMessage>
    );
  }

  return lookup.status === "environment-not-found" ? (
    <ErrorMessage title={"Specified environment not found"} compact>
      The specified environment ({environmentId}) could not be found. It may have been archived or
      may not exist.
    </ErrorMessage>
  ) : null;
};
